import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { api } from '../../../lib/api-client';
import {
  WS_EVENTS,
  type ConversationSequenceFlagRaisedPayload,
  type ConversationSequenceMemberAddedPayload,
} from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

import type { ConversationDetail } from '../../../lib/types';

export interface SequenceFlags {
  highVolumeFlag: boolean;
  allAbandonedFlag: boolean;
  escalatingSentimentFlag: boolean;
  crossChannelInsistenceFlag: boolean;
}

export interface ConversationSequenceState {
  status: 'idle' | 'loading' | 'ready' | 'error' | 'disabled';
  /** Posição da conversa atual na sequence ativa (1-indexed). `null` quando conversa fora de sequence. */
  position: number | null;
  /** Total de conversas na sequence ativa. `null` quando fora. */
  total: number | null;
  /** Janela canônica por bond.type (ADR-055) — informa banner. */
  windowHours: number | null;
  /** 4 flags pré-computadas pelo Engine (Fatia F). */
  flags: SequenceFlags;
  /** ID da sequence ativa — null quando ausente. */
  sequenceId: string | null;
}

const EMPTY_FLAGS: SequenceFlags = {
  highVolumeFlag: false,
  allAbandonedFlag: false,
  escalatingSentimentFlag: false,
  crossChannelInsistenceFlag: false,
};

const EMPTY_STATE: ConversationSequenceState = {
  status: 'idle',
  position: null,
  total: null,
  windowHours: null,
  flags: EMPTY_FLAGS,
  sequenceId: null,
};

/**
 * useSequence — PR-B.2 (Item 89).
 *
 * Consome `GET /v1/persons/:personId/sequences?bondId=X&status=active` e
 * resolve a sequence ativa da conversa em foco (via match
 * `conversationSequenceMember.conversationId`).
 *
 * **Pré-requisitos canônicos (ADR-055):**
 *   - `conversation.personId` presente (sem person → não há sequence — typical
 *     NetComet anônimo).
 *   - `conversation.bondId` presente (sequence é amarrada Bond+Person).
 *   - Capability `conversation-sequences` (Team+).
 *
 * Faltando qualquer um → retorna estado vazio com `status='disabled'`.
 *
 * **WebSocket refresh canônico (Fatia F):**
 *   - `conversation_sequence.flag-raised` → invalida ao chegar para a sequence
 *     em foco.
 *   - `conversation_sequence.member-added` → invalida quando nova conversa
 *     entra (pode incrementar `position` da atual relativamente).
 *
 * Pattern espelha `useChecklists` (Fatia E): query + subscribe + filtro
 * client-side por conversationId.
 */
export function useSequence(conversation: ConversationDetail): ConversationSequenceState {
  const hasCapability = useCapability('conversation-sequences');
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();

  const personId = conversation.personId;
  const bondId = conversation.bondId;
  const conversationId = conversation.id;

  const enabled = hasCapability && personId !== null && bondId !== null;
  const queryKey = ['conversation-sequence', personId, bondId];

  const queryResult = useQuery({
    queryKey,
    enabled,
    queryFn: () => {
      if (personId === null || bondId === null) return Promise.resolve({ data: [] });
      return api.listSequencesForPerson(personId, { bondId, status: 'active' });
    },
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const unsubFlag = subscribe<ConversationSequenceFlagRaisedPayload>(
      WS_EVENTS.CONVERSATION_SEQUENCE_FLAG_RAISED,
      (envelope) => {
        if (envelope.data.bondId !== bondId) return;
        void queryClient.invalidateQueries({ queryKey });
      },
    );
    const unsubMember = subscribe<ConversationSequenceMemberAddedPayload>(
      WS_EVENTS.CONVERSATION_SEQUENCE_MEMBER_ADDED,
      (envelope) => {
        // Payload member-added não traz `bondId`; invalida via `conversationId`
        // (atual ou nova entrante na sequence em foco) — granularidade
        // suficiente para evitar refetch fora-de-escopo.
        if (envelope.data.conversationId !== conversationId) return;
        void queryClient.invalidateQueries({ queryKey });
      },
    );
    return () => {
      unsubFlag();
      unsubMember();
    };
  }, [enabled, bondId, subscribe, queryClient, queryKey]);

  if (!enabled) {
    return { ...EMPTY_STATE, status: 'disabled' };
  }
  if (queryResult.isError) return { ...EMPTY_STATE, status: 'error' };
  if (queryResult.isLoading) return { ...EMPTY_STATE, status: 'loading' };

  const list = queryResult.data?.data ?? [];
  // Resolve a sequence em que a conversa em foco é membro.
  const active = list.find((s) => s.members.some((m) => m.conversationId === conversationId));
  if (!active) {
    return { ...EMPTY_STATE, status: 'ready' };
  }
  // position = orderIndex + 1 (orderIndex é 0-indexed conforme service).
  const member = active.members.find((m) => m.conversationId === conversationId);
  const position = member !== undefined ? member.orderIndex + 1 : null;
  const total = active.totalConversations;
  const windowHours = Math.round(active.windowDurationSeconds / 3600);

  return {
    status: 'ready',
    position,
    total,
    windowHours,
    flags: active.flags,
    sequenceId: active.id,
  };
}
